import React, { useEffect, useState } from "react";
import api from "../api/api";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../context/AuthContext";
import { Send, Loader } from "react-feather";
import {useTranslation } from "react-i18next";
const Broadcast = (prop) => {
  const { user } = useAuth();
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [originalAgencies, setOriginalAgencies] = useState([]);
  const [loadingSendInv, setLoadingSendInv] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [broadcastToAll, setBroadcastToAll] = useState(false);

  const { t } = useTranslation();
  // Form validation schema
  const schema = yup.object({
    agency_id: yup
      .array()
      .when('broadcast_to_all', {
        is: true,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.min(1, "Please select at least one agency").required("At least one agency is required")
      }),
    broadcast_to_all: yup.boolean(),
  });

  const {
    reset,
    handleSubmit,
    register,
    setError,
    setValue, // Added setValue here
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // Fetch agencies list
  const fetchDataAgencies = async () => {
    clearErrors();
    setErrorMessage(null);
    
    try {
      const response = await api.get('/vendor-agency-list');

      if (response.status === 200 && Array.isArray(response.data.data)) {
        setAgencies(response.data.data);
        setOriginalAgencies(response.data.data);
      } else {
        setErrorMessage("Failed to load agencies list");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error fetching agencies");
      console.error("Error fetching agencies:", error);
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (agencyId) => {
    const newSelection = selectedAgencies.includes(agencyId)
      ? selectedAgencies.filter(id => id !== agencyId)
      : [...selectedAgencies, agencyId];
    
    setSelectedAgencies(newSelection);
    // Update form value for validation
    setValue("agency_id", newSelection); // Now properly defined
  };

  // Handle broadcast to all checkbox
  const handleBroadcastToAllChange = (e) => {
    const isChecked = e.target.checked;
    setBroadcastToAll(isChecked);
    setValue("broadcast_to_all", isChecked);
    
    // If broadcast to all is checked, clear individual selections
    if (isChecked) {
      setSelectedAgencies([]);
      setValue("agency_id", []);
    }
  };



  // Submit handler
  const onSubmit = async (data) => {
    setLoadingSendInv(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    try {
      const requestData = {
        biodata_id: prop.clickedBiodata.id,
        broadcast_to_all: broadcastToAll,
      };

      // Only send agency_ids if not broadcasting to all
      if (!broadcastToAll) {
        requestData.agency_ids = selectedAgencies;
      }

      const response = await api.post('/biodata-supplier-agency-save/', requestData);

      if (response.status === 201) {
        const message = broadcastToAll 
          ? "Broadcast sent successfully to all agencies"
          : `Broadcast sent successfully to ${selectedAgencies.length} agency(ies)`;
        
        setSuccessMessage(message);
        // Reset form after successful submission
        setSelectedAgencies([]);
        setBroadcastToAll(false);
        document.querySelector('#broadcast_modal .btn-close')?.click();
        reset();
      } else {
        setErrorMessage(response.data?.message || "Failed to send broadcast");
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error sending broadcast");
      console.error("Broadcast error:", error);
    } finally {
      setLoadingSendInv(false);
    }
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    if(searchTerm) {
      const filteredAgencies = originalAgencies.filter(agency =>
        agency.name.toLowerCase().includes(searchTerm) ||
        agency.email.toLowerCase().includes(searchTerm)
      );
      setAgencies(filteredAgencies);
    } else {
      setAgencies(originalAgencies);
    }
  };

  useEffect(() => {
    fetchDataAgencies();
  }, []);

  return (
    <>
      <style>
        {`
          .hover-bg-light:hover {
            background-color: #f8f9fa !important;
          }
          .transition-all {
            transition: all 0.3s ease;
          }
          .agency-list .form-check:last-child {
            border-bottom: none !important;
          }
          .card:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
          }
        `}
      </style>
      <div className="modal custom-modal modal-lg fade" id="broadcast_modal" role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-md">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <div className="form-header modal-header-title text-start mb-0">
              <h4 className="mb-0">{t("Broadcast")}</h4>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              data-bs-dismiss="modal" 
              aria-label="Close"
              onClick={() => {
                setSuccessMessage(null);
                setErrorMessage(null);
                setBroadcastToAll(false);
                setSelectedAgencies([]);
              }}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-12 mx-1">
                  <div className="card-body">
                    <div className="form-groups-item">
                      {/* Broadcast to All Option */}
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <div className={`card border-0 shadow-sm ${broadcastToAll ? 'border-primary bg-primary bg-opacity-10' : 'bg-light'} transition-all`}>
                            <div className="card-body p-4">
                              <div className="form-check d-flex align-items-start">
                                <input
                                  className="form-check-input me-3 mt-1"
                                  type="checkbox"
                                  id="broadcast-to-all"
                                  checked={broadcastToAll}
                                  onChange={handleBroadcastToAllChange}
                                  style={{ transform: 'scale(1.2)' }}
                                />
                                <div className="flex-grow-1">
                                  <label className="form-check-label fw-bold text-dark mb-2 d-block" htmlFor="broadcast-to-all" style={{ fontSize: '1.1rem' }}>
                                    <i className="fas fa-broadcast-tower me-2 text-primary"></i>
                                    {t("Broadcast with Agencies")}
                                  </label>
                                  <p className="text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                                    {t("Maximize your reach by sharing this biodata with all the agencies on your list.")}
                                  </p>
                                  <div className="d-flex align-items-center">
                                    <span className="badge bg-success bg-opacity-20 text-light me-2">
                                      <i className="fas fa-rocket me-1"></i>
                                      {t("Maximum Reach")}
                                    </span>
                                    <span className="badge bg-info bg-opacity-20 text-light me-2">
                                      <i className="fas fa-globe me-1"></i>
                                      {t("All listed Agencies")}
                                    </span>
                                  </div>
                                  {broadcastToAll && (
                                    <div className="mt-3 p-2 bg-success bg-opacity-10 rounded">
                                      <small className="text-light fw-medium">
                                        <i className="fas fa-check-circle me-1"></i>
                                        {t("Your biodata will be sent to all related agencies")}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Agency Selection - only show if not broadcasting to all */}
                      {!broadcastToAll && (
                        <div className="row">
                          <div className="col-md-12">
                            <div className="card border-0 shadow-sm">
                              <div className="card-body">
                                <div className="d-flex align-items-center mb-3">
                                  <i className="fas fa-users me-2 text-primary"></i>
                                  <label className="form-label mb-0 fw-bold">{t("Select Specific Partner Agencies")}</label>
                                </div>
                                
                                {/* Search bar */}
                                <div className="form-group mb-3">
                                  <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                      <i className="fas fa-search text-muted"></i>
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control border-start-0"
                                      placeholder={t("Search agency by name or email")}
                                      onChange={handleSearch}
                                    />
                                  </div>
                                </div>

                                {agencies.length > 0 ? (
                                  <div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <small className="text-muted">
                                        {selectedAgencies.length} of {agencies.length} agencies selected
                                      </small>
                                    </div>
                                    <div className="agency-list border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                      {agencies.map(agency => (
                                        <div key={agency.id} className="form-check p-3 border-bottom d-flex align-items-start hover-bg-light ms-2">
                                          <input
                                            className="form-check-input me-3 mt-1"
                                            type="checkbox"
                                            id={`agency-${agency.id}`}
                                            checked={selectedAgencies.includes(agency.id)}
                                            onChange={() => handleCheckboxChange(agency.id)}
                                          />
                                          <div className="flex-grow-1">
                                            <label className="form-check-label fw-medium text-dark" htmlFor={`agency-${agency.id}`}>
                                              {agency.name}
                                            </label>
                                            <div className="text-muted small mt-1">{agency.email}</div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    {errors.agency_id && (
                                      <small className="text-danger mt-2 d-block">
                                        <i className="fas fa-exclamation-circle me-1"></i>
                                        {errors.agency_id.message}
                                      </small>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center py-4">
                                    <i className="fas fa-users-slash text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                                    <div className="text-muted">{t("You don't have any agency partners yet.")}</div>
                                    <small className="text-muted">{t("Consider using 'Broadcast to All' to reach more agencies.")}</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
             
            {successMessage && ( <small  style={{color:"green"}}>
              {successMessage}
                </small> )}
             
             
                {errorMessage && (<small  style={{color:"red"}}>
                {errorMessage}
                </small> )} 
             
                <div className="d-flex justify-content-end w-100 mt-2">
              <button 
                type="button" 
                data-bs-dismiss="modal" 
                className="btn btn-back cancel-btn me-2"
                onClick={() => {
                  setSuccessMessage(null);
                  setErrorMessage(null);
                  setBroadcastToAll(false);
                  setSelectedAgencies([]);
                }}
              >
                {t("Cancel")}
              </button>
              
              <button 
                type="submit" 
                disabled={loadingSendInv || (!broadcastToAll && selectedAgencies.length === 0)}
                className="btn btn-primary"
              >
                {loadingSendInv ? (
                  <>
                    {t("Sending...")} <Loader className="spinner-border spinner-border-sm" />
                  </>
                ) : (
                  <>
                    {t("Send")} 
                  </>
                )}
              </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default Broadcast;